import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { AlertTriangle, CalendarX, Check, Plus, Trash2, X } from 'lucide-react';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { ConfirmActionModal } from '@/components/patient/PatientTabActions';
import {
  CourseBreadcrumb,
  CourseTabPanel,
  CourseWindowScale,
} from '@/components/patient/course/CourseChrome';
import { CourseSlotWeekPicker } from '@/components/patient/course/CourseSlotWeekPicker';
import { QuestionnaireMultiPicker } from '@/components/questionnaires/QuestionnaireMultiPicker';
import { toQuestionnairePickerItems } from '@/lib/questionnairePickerLabel';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { useClinicRoles } from '@/hooks/useClinicRoles';
import { filterPractitionerMembers, withStickySelectedMembers } from '@/lib/practitionerRole';
import { useFormsTemplateList, type FormsTemplateSummary } from '@/hooks/useFormsTemplates';
import { useResources } from '@/hooks/useSchedulingResources';
import { useServicesList } from '@/hooks/useServices';
import {
  useAddTreatmentCourseStep,
  useCancelTreatmentCourseStep,
  useRemoveTreatmentCourseStep,
  useSaveTreatmentCourseStep,
  useTreatmentCourse,
} from '@/hooks/useTreatmentCourses';
import {
  bookedStepDraftChangeCount,
  canBookBookedStep,
  canCancelBookedStep,
  canEditBookedCourse,
  draftFromBookedStep,
  saveStepPayload,
  stepDisplayTitle,
  type BookedStepDraft,
} from '@/lib/bookedTreatmentCourse';
import { computeCourseIntervalWindow, daysOutsideCourseWindow } from '@/lib/courseIntervalWindow';
import { practitionerDisplayName, previousBookedStartsAtForStep } from '@/lib/courseStepSlotPicker';
import {
  clinicMemberNameById,
  filterAllowedQuestionnaires,
  resolveCoursePersonName,
} from '@/lib/courseUiHelpers';
import {
  formatCourseDate,
  formatCourseDateTime,
  formatCourseWindowRangeWithYear,
} from '@/lib/courseDateFormat';
import { resolveQuestionnaireDisplayName } from '@/lib/questionnaireDisplayName';
import {
  createQuestionnaireAutomationDraft,
  displayToMinutes,
  minutesToDisplay,
  type QuestionnaireAutomationDraft,
  type QuestionnairePhase,
  type TimingUnit,
} from '@/lib/preVisitQuestionnaireTiming';
import { civilDateFromIso } from '@/lib/treatmentCourseWizard';
import { FIELD_CONTROL } from '@/components/ui/fieldClasses';
import { DURATION_OPTIONS } from '@/pages/settings/treatmentCourseEditorFields';
import { FORM_LABEL_TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { Modal } from '@/components/ui/Modal';
import { initialsFromName } from '@/lib/initials';
import type { BookedTreatmentCourseStep } from '@/types/treatmentCourses';

const EDIT_SECTIONS = [
  'appointment',
  'time',
  'practitioners',
  'resources',
  'questionnaires',
] as const;

type EditSection = (typeof EDIT_SECTIONS)[number];

function sectionChanged(
  id: EditSection,
  baseline: BookedStepDraft,
  draft: BookedStepDraft,
): boolean {
  switch (id) {
    case 'appointment':
      return (
        baseline.title.trim() !== draft.title.trim() ||
        baseline.durationMinutes !== draft.durationMinutes ||
        baseline.intervalValue !== draft.intervalValue ||
        baseline.intervalUnit !== draft.intervalUnit ||
        baseline.intervalToleranceValue !== draft.intervalToleranceValue ||
        baseline.intervalToleranceUnit !== draft.intervalToleranceUnit
      );
    case 'time':
      return (baseline.startsAt ?? '') !== (draft.startsAt ?? '');
    case 'practitioners':
      return (
        [...baseline.practitionerIds].sort().join() !== [...draft.practitionerIds].sort().join()
      );
    case 'resources':
      return [...baseline.resourceIds].sort().join() !== [...draft.resourceIds].sort().join();
    case 'questionnaires':
      return JSON.stringify(baseline.automations) !== JSON.stringify(draft.automations);
    default: {
      const _exhaustive: never = id;
      return _exhaustive;
    }
  }
}

function questionnaireName(
  templateId: string,
  templates: Array<{
    id: string;
    display_name_da: string | null;
    display_name_en: string | null;
  }>,
  language: string,
  fallback: string,
): string {
  const tpl = templates.find((item) => item.id === templateId);
  return resolveQuestionnaireDisplayName({
    templateId,
    snapshotName: language.startsWith('en') ? tpl?.display_name_en : tpl?.display_name_da,
    locale: language,
    templatesById: tpl
      ? {
          [tpl.id]: {
            id: tpl.id,
            display_name_da: tpl.display_name_da ?? '',
            display_name_en: tpl.display_name_en ?? '',
          },
        }
      : {},
    fallback,
  });
}

export function BookedTreatmentCourseEditor({
  clinicId,
  courseId,
  canEdit,
  patientName: _patientName,
  initialStepId,
  onBack,
  onOpenCourse,
  onOpenStep,
  onOpenList,
}: {
  clinicId: string;
  courseId: string;
  canEdit: boolean;
  patientName?: string | null;
  initialStepId?: string | null;
  onBack: () => void;
  onOpenCourse?: () => void;
  onOpenStep?: (stepId: string) => void;
  onOpenList?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { data: course, isLoading, error, refetch } = useTreatmentCourse(clinicId, courseId);
  const saveStep = useSaveTreatmentCourseStep(clinicId);
  const addStep = useAddTreatmentCourseStep(clinicId);
  const cancelStep = useCancelTreatmentCourseStep(clinicId);
  const removeStep = useRemoveTreatmentCourseStep(clinicId);
  const { data: members } = useClinicMembers(clinicId);
  const { data: roles, isSuccess: rolesReady } = useClinicRoles(clinicId);
  const { data: resources } = useResources(clinicId);
  const { data: services } = useServicesList(clinicId, { includeInactive: true });
  const { data: templateLibrary, isLoading: templatesLoading } = useFormsTemplateList(clinicId);

  const [selectedId, setSelectedId] = useState<string | null>(initialStepId ?? null);
  const [section, setSection] = useState<EditSection>('time');
  const [draft, setDraft] = useState<BookedStepDraft | null>(null);
  const [baseline, setBaseline] = useState<BookedStepDraft | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addServiceId, setAddServiceId] = useState('');
  const [cancelTarget, setCancelTarget] = useState<BookedTreatmentCourseStep | null>(null);
  const [removeTarget, setRemoveTarget] = useState<BookedTreatmentCourseStep | null>(null);
  const [showResources, setShowResources] = useState(false);
  const [pickerPhase, setPickerPhase] = useState<QuestionnairePhase | null>(null);

  const editable = Boolean(course && canEditBookedCourse(course.status, canEdit));
  const selected = course?.steps.find((step) => step.id === selectedId) ?? course?.steps[0] ?? null;

  useEffect(() => {
    if (!course?.steps.length) return;
    if (!selectedId || !course.steps.some((step) => step.id === selectedId)) {
      setSelectedId(
        initialStepId && course.steps.some((step) => step.id === initialStepId)
          ? initialStepId
          : course.steps[0].id,
      );
    }
  }, [course, initialStepId, selectedId]);

  useEffect(() => {
    if (!selected) {
      setDraft(null);
      setBaseline(null);
      return;
    }
    const next = draftFromBookedStep(selected);
    setDraft(next);
    setBaseline(next);
    setShowResources(next.resourceIds.length > 0);
  }, [selected?.id, selected?.starts_at, selected?.status]);

  const memberNames = useMemo(() => clinicMemberNameById(members), [members]);
  const practitionerChoices = useMemo(() => {
    const all = members ?? [];
    if (!rolesReady) return all;
    return withStickySelectedMembers(
      filterPractitionerMembers(all, roles ?? []),
      all,
      draft?.practitionerIds ?? [],
    );
  }, [draft?.practitionerIds, members, roles, rolesReady]);
  const formsTemplates = [...(templateLibrary?.platform ?? []), ...(templateLibrary?.clinic ?? [])];
  const pickerTemplates = filterAllowedQuestionnaires(
    formsTemplates,
    selected?.allowed_forms_template_ids,
  );
  const previousStartsAt = selected
    ? previousBookedStartsAtForStep(course?.steps ?? [], selected.sort_order)
    : null;
  const firstStep = Boolean(selected && selected.sort_order <= 1);
  const window =
    selected && draft && !firstStep
      ? computeCourseIntervalWindow({
          previousStartsAt,
          intervalValue: draft.intervalValue,
          intervalUnit: draft.intervalUnit,
          toleranceValue: draft.intervalToleranceValue,
          toleranceUnit: draft.intervalToleranceUnit,
        })
      : null;
  const changeCount = draft && baseline ? bookedStepDraftChangeCount(baseline, draft) : 0;
  const timeChanged = Boolean(draft && baseline && draft.startsAt !== baseline.startsAt);
  const outsideDays = daysOutsideCourseWindow(civilDateFromIso(draft?.startsAt ?? null), window);
  const canPickTime = Boolean(
    selected && editable && (canBookBookedStep(selected.status) || selected.status === 'upcoming'),
  );

  async function persist(next: BookedStepDraft) {
    if (!selected || !editable) return;
    try {
      await saveStep.mutateAsync({ stepId: selected.id, payload: saveStepPayload(next) });
      setBaseline(next);
      toast.success(t('treatment_courses.patient_toast_saved'));
    } catch (err) {
      console.error(err);
      toast.error(t('treatment_courses.patient_toast_error'));
    }
  }

  function goTo(id: EditSection) {
    setSection(id);
    document.getElementById(`fl-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  if (isLoading) {
    return <p className="hint">{t('treatment_courses.patient_loading')}</p>;
  }
  if (error || !course) {
    return (
      <Banner
        tone="error"
        title={t('treatment_courses.patient_load_error')}
        action={
          <Button type="button" variant="secondary" onClick={() => void refetch()}>
            {t('app.retry')}
          </Button>
        }
      />
    );
  }

  const stepTitle = selected
    ? stepDisplayTitle(selected, t('treatment_courses.step_no_service'))
    : t('treatment_courses.patient_pick_step');

  return (
    <CourseTabPanel testId="booked-treatment-course-editor">
      <CourseBreadcrumb
        items={[
          { label: t('treatment_courses.crumb_all'), onClick: onOpenList ?? onBack },
          {
            label: course.template_name || t('treatment_courses.summary_untitled'),
            onClick: onOpenCourse ?? onBack,
          },
          {
            label: selected
              ? t('treatment_courses.step_n', { n: selected.sort_order })
              : t('treatment_courses.section_steps'),
            onClick: selected ? () => (onOpenStep ? onOpenStep(selected.id) : onBack()) : undefined,
          },
          { label: t('treatment_courses.crumb_edit') },
        ]}
      />

      <div className="tf-h">
        <div className="hgrp">
          <div className="hl">
            <h2 id="h-fl">
              {t('treatment_courses.edit_step_title', {
                n: selected?.sort_order ?? 0,
                total: course.steps.length,
                name: stepTitle,
              })}
            </h2>
          </div>
          <p className="fl-sub">{t('treatment_courses.edit_step_hint')}</p>
        </div>
      </div>

      {selected && draft ? (
        <div className="ed-grid">
          <nav className="snav" aria-label={t('treatment_courses.section_steps')}>
            {EDIT_SECTIONS.map((id) => {
              const current = section === id;
              const count =
                id === 'practitioners'
                  ? draft.practitionerIds.length
                  : id === 'resources'
                    ? draft.resourceIds.length
                    : id === 'questionnaires'
                      ? draft.automations.length
                      : null;
              const changed = baseline ? sectionChanged(id, baseline, draft) : false;
              return (
                <a
                  key={id}
                  href={`#fl-${id}`}
                  aria-current={current ? 'true' : undefined}
                  onClick={(event) => {
                    event.preventDefault();
                    goTo(id);
                  }}
                >
                  {id === 'appointment'
                    ? t('treatment_courses.tab_appointment')
                    : id === 'time'
                      ? t('treatment_courses.section_time')
                      : id === 'practitioners'
                        ? t('treatment_courses.tab_practitioners')
                        : id === 'resources'
                          ? t('treatment_courses.tab_resources')
                          : t('treatment_courses.tab_questionnaires')}
                  {changed ? (
                    <span className="chg" aria-label={t('treatment_courses.changed')} />
                  ) : null}
                  {!changed && count != null ? <span className="n">{count}</span> : null}
                </a>
              );
            })}
            <hr />
            {editable && canCancelBookedStep(selected.status) ? (
              <button type="button" className="dang" onClick={() => setCancelTarget(selected)}>
                <CalendarX className="i sm" aria-hidden />
                {t('treatment_courses.patient_cancel_step')}
              </button>
            ) : null}
            {editable && course.steps.length > 1 ? (
              <button type="button" className="dang" onClick={() => setRemoveTarget(selected)}>
                <Trash2 className="i sm" aria-hidden />
                {t('treatment_courses.remove_step')}
              </button>
            ) : null}
          </nav>

          <div>
            <section className="fsec2" id="fl-appointment">
              <h3>{t('treatment_courses.tab_appointment')}</h3>
              <div className="body">
                <div className="fgrid">
                  <div className="field">
                    <label htmlFor="fl-title">{t('treatment_courses.field_step_title')}</label>
                    <Input
                      id="fl-title"
                      value={draft.title}
                      disabled={!editable}
                      onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                    />
                  </div>
                  <div className="field">
                    <span className="lbl">{t('treatment_courses.field_duration')}</span>
                    <Select
                      value={draft.durationMinutes}
                      disabled={!editable}
                      onChange={(event) =>
                        setDraft({ ...draft, durationMinutes: Number(event.target.value) })
                      }
                    >
                      {(DURATION_OPTIONS.includes(draft.durationMinutes)
                        ? DURATION_OPTIONS
                        : [...DURATION_OPTIONS, draft.durationMinutes]
                      ).map((minutes) => (
                        <option key={minutes} value={minutes}>
                          {t('treatment_courses.duration_minutes', { minutes })}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="field full">
                    <span className="lbl">
                      {firstStep
                        ? t('treatment_courses.field_interval')
                        : t('treatment_courses.field_interval_after', {
                            n: selected.sort_order - 1,
                          })}
                    </span>
                    {firstStep ? (
                      <p className="hint">{t('treatment_courses.interval_first_step_hint')}</p>
                    ) : (
                      <>
                        <div
                          className="ivl-ed num flex flex-wrap items-center gap-2"
                          data-testid="edit-step-interval"
                        >
                          <span>+</span>
                          <input
                            type="number"
                            min={0}
                            max={5200}
                            className={cn(FIELD_CONTROL, 'w-16 text-center')}
                            value={draft.intervalValue}
                            disabled={!editable}
                            aria-label={t('treatment_courses.field_interval')}
                            onChange={(event) =>
                              setDraft({
                                ...draft,
                                intervalValue: Math.max(
                                  0,
                                  Math.min(5200, Number(event.target.value) || 0),
                                ),
                              })
                            }
                          />
                          <Select
                            className="w-auto min-w-[7rem]"
                            value={draft.intervalUnit}
                            disabled={!editable}
                            aria-label={t('treatment_courses.field_interval')}
                            onChange={(event) =>
                              setDraft({
                                ...draft,
                                intervalUnit: event.target.value === 'weeks' ? 'weeks' : 'days',
                              })
                            }
                          >
                            <option value="weeks">
                              {t('treatment_courses.interval_unit_weeks_other')}
                            </option>
                            <option value="days">
                              {t('treatment_courses.interval_unit_days_other')}
                            </option>
                          </Select>
                          <span>±</span>
                          <input
                            type="number"
                            min={0}
                            max={5200}
                            className={cn(FIELD_CONTROL, 'w-16 text-center')}
                            value={draft.intervalToleranceValue}
                            disabled={!editable}
                            aria-label={t('treatment_courses.field_tolerance')}
                            onChange={(event) =>
                              setDraft({
                                ...draft,
                                intervalToleranceValue: Math.max(
                                  0,
                                  Math.min(5200, Number(event.target.value) || 0),
                                ),
                              })
                            }
                          />
                          <Select
                            className="w-auto min-w-[7rem]"
                            value={draft.intervalToleranceUnit}
                            disabled={!editable}
                            aria-label={t('treatment_courses.field_tolerance')}
                            onChange={(event) =>
                              setDraft({
                                ...draft,
                                intervalToleranceUnit:
                                  event.target.value === 'weeks' ? 'weeks' : 'days',
                              })
                            }
                          >
                            <option value="days">
                              {t('treatment_courses.interval_unit_days_other')}
                            </option>
                            <option value="weeks">
                              {t('treatment_courses.interval_unit_weeks_other')}
                            </option>
                          </Select>
                          <span className="muted">
                            {t('treatment_courses.interval_after_named', {
                              n: selected.sort_order - 1,
                              when: formatCourseDate(previousStartsAt, i18n.language),
                            })}
                          </span>
                        </div>
                        {window ? (
                          <div className="winbox num">
                            <span>
                              <b>
                                {t('treatment_courses.window_label', { range: '' }).replace(
                                  /\s+$/,
                                  ':',
                                )}
                              </b>{' '}
                              {formatCourseWindowRangeWithYear(
                                window.startIso,
                                window.endIso,
                                i18n.language,
                              )}
                            </span>
                            <CourseWindowScale window={window} startsAt={draft.startsAt} />
                            <span className="muted">
                              {t('treatment_courses.window_times_hint')}
                            </span>
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </section>

            <section className="fsec2" id="fl-time">
              <h3>{t('treatment_courses.section_time')}</h3>
              <p className="hint">{t('treatment_courses.slot_week_hint')}</p>
              <div className="body">
                {baseline?.startsAt || draft.startsAt ? (
                  <div
                    className={cn('cur-new num', outsideDays != null && outsideDays > 0 && 'bad')}
                  >
                    {baseline?.startsAt ? (
                      <span className="old">
                        {formatCourseDateTime(baseline.startsAt, i18n.language)}
                      </span>
                    ) : null}
                    {timeChanged && draft.startsAt ? (
                      <>
                        <svg className="i sm" viewBox="0 0 24 24" aria-hidden>
                          <path d="M5 12h14" />
                          <path d="m12 5 7 7-7 7" />
                        </svg>
                        <span className="new">
                          {formatCourseDateTime(draft.startsAt, i18n.language)}
                        </span>
                      </>
                    ) : null}
                    {window && draft.startsAt && outsideDays === 0 ? (
                      <span className="chip chip-green">
                        <Check className="i xs" aria-hidden />
                        {t('treatment_courses.in_window')}
                      </span>
                    ) : null}
                    {window && outsideDays != null && outsideDays > 0 ? (
                      <span className="chip chip-amber">
                        <AlertTriangle className="i xs" aria-hidden />
                        {t('treatment_courses.out_of_window')}
                      </span>
                    ) : null}
                  </div>
                ) : null}
                {window && outsideDays != null && outsideDays > 0 ? (
                  <div className="warn-inline badinfo">
                    <AlertTriangle className="i sm" aria-hidden />
                    <span>
                      {t(
                        civilDateFromIso(draft.startsAt) &&
                          civilDateFromIso(draft.startsAt)! < window.startCivil
                          ? 'treatment_courses.savebar_outside_before'
                          : 'treatment_courses.savebar_outside',
                        {
                          count: outsideDays,
                          range: formatCourseWindowRangeWithYear(
                            window.startIso,
                            window.endIso,
                            i18n.language,
                          ),
                        },
                      )}
                    </span>
                  </div>
                ) : null}
                {canPickTime ? (
                  <CourseSlotWeekPicker
                    clinicId={clinicId}
                    bookedStepId={selected.id}
                    previousStartsAt={previousStartsAt}
                    window={window}
                    selectedStartsAt={draft.startsAt}
                    selectedPractitionerIds={draft.practitionerIds}
                    currentStartsAt={baseline?.startsAt}
                    assignedPractitionerIds={
                      selected.status === 'unscheduled' ? [] : draft.practitionerIds
                    }
                    enabled={canPickTime}
                    onSelectSlot={(slot) =>
                      setDraft({
                        ...draft,
                        startsAt: slot.starts_at,
                        endsAt: slot.ends_at,
                        durationMinutes: slot.duration_minutes,
                        practitionerIds: slot.practitioner_ids,
                        resourceIds: slot.resource_ids.length
                          ? slot.resource_ids
                          : draft.resourceIds,
                      })
                    }
                  />
                ) : (
                  <p className="hint">{t('treatment_courses.book_not_booked')}</p>
                )}
              </div>
            </section>

            <section className="fsec2" id="fl-practitioners">
              <h3>{t('treatment_courses.tab_practitioners')}</h3>
              <p className="hint">{t('treatment_courses.practitioners_first_hint')}</p>
              <div className="body">
                <div className="pchips2">
                  {practitionerChoices.map((member) => {
                    const name =
                      resolveCoursePersonName(member.user_id, memberNames) ||
                      practitionerDisplayName(
                        member.name || member.email,
                        t('treatment_courses.practitioner_fallback'),
                      );
                    const on = draft.practitionerIds.includes(member.user_id);
                    return (
                      <button
                        key={member.user_id}
                        type="button"
                        className={cn('pcheck', on && 'on')}
                        disabled={!editable}
                        aria-pressed={on}
                        onClick={() =>
                          setDraft({
                            ...draft,
                            practitionerIds: on
                              ? draft.practitionerIds.filter((id) => id !== member.user_id)
                              : [...draft.practitionerIds, member.user_id],
                          })
                        }
                      >
                        <span className="av">{initialsFromName(name) || name.slice(0, 2)}</span>
                        {name}
                        {on ? <Check className="i sm" aria-hidden /> : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            <section className="fsec2" id="fl-resources">
              <h3>{t('treatment_courses.tab_resources')}</h3>
              <div className="body">
                {!showResources && draft.resourceIds.length === 0 ? (
                  <div className="emp">
                    <span className="grow">{t('treatment_courses.resources_empty')}</span>
                    {editable ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setShowResources(true)}
                      >
                        <Plus className="i sm" aria-hidden />
                        {t('treatment_courses.add_resource')}
                      </Button>
                    ) : null}
                  </div>
                ) : (
                  <div className="pchips2">
                    {(resources ?? []).map((resource) => {
                      const on = draft.resourceIds.includes(resource.id);
                      return (
                        <button
                          key={resource.id}
                          type="button"
                          className={cn('pcheck', on && 'on')}
                          disabled={!editable}
                          aria-pressed={on}
                          onClick={() =>
                            setDraft({
                              ...draft,
                              resourceIds: on
                                ? draft.resourceIds.filter((id) => id !== resource.id)
                                : [...draft.resourceIds, resource.id],
                            })
                          }
                        >
                          {resource.name}
                          {on ? <Check className="i sm" aria-hidden /> : null}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>

            <section className="fsec2 sk" id="fl-questionnaires">
              <div className="sp-h">
                <h3>{t('treatment_courses.tab_questionnaires')}</h3>
                {editable ? (
                  <div className="sp-act">
                    <Button
                      type="button"
                      variant="tertiary"
                      size="sm"
                      aria-haspopup="dialog"
                      aria-expanded={pickerPhase != null}
                      onClick={() => setPickerPhase('after_appointment')}
                    >
                      <Plus className="i sm" aria-hidden />
                      {t('treatment_courses.add_questionnaire')}
                    </Button>
                    <PatientQuestionnairePicker
                      open={pickerPhase != null}
                      phase={pickerPhase ?? 'after_appointment'}
                      step={selected}
                      stepIndex={selected.sort_order}
                      stepCount={course.steps.length}
                      templates={pickerTemplates}
                      language={i18n.language}
                      lockedIds={draft.automations
                        .filter((row) => row.phase === (pickerPhase ?? 'after_appointment'))
                        .map((row) => row.forms_template_id)}
                      onClose={() => setPickerPhase(null)}
                      onConfirm={(ids) => {
                        if (!pickerPhase) return;
                        const extra = ids
                          .filter(
                            (id) =>
                              !draft.automations.some(
                                (row) => row.forms_template_id === id && row.phase === pickerPhase,
                              ),
                          )
                          .map((id, index) =>
                            createQuestionnaireAutomationDraft({
                              forms_template_id: id,
                              phase: pickerPhase,
                              sort_order: draft.automations.length + index,
                            }),
                          );
                        if (extra.length) {
                          setDraft({ ...draft, automations: [...draft.automations, ...extra] });
                        }
                      }}
                    />
                  </div>
                ) : null}
              </div>
              {selected.allowed_forms_template_ids.length > 0 ? (
                <p className="hint">
                  {t('treatment_courses.patient_allowed_hint', {
                    count: selected.allowed_forms_template_ids.length,
                  })}
                </p>
              ) : null}
              <div className="body">
                {(['before_appointment', 'after_appointment'] as const).map((phase) => {
                  const rows = draft.automations.filter((row) => row.phase === phase);
                  return (
                    <div key={phase}>
                      <p className="qsub">
                        {phase === 'before_appointment'
                          ? t('treatment_courses.q_before')
                          : t('treatment_courses.q_after')}
                      </p>
                      {rows.length === 0 ? (
                        <div className="emp">
                          <span className="grow">
                            {phase === 'before_appointment'
                              ? t('treatment_courses.q_empty_before')
                              : t('treatment_courses.q_empty_after')}
                          </span>
                          {editable ? (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={() => setPickerPhase(phase)}
                            >
                              <Plus className="i sm" aria-hidden />
                              {t('treatment_courses.add_questionnaire')}
                            </Button>
                          ) : null}
                        </div>
                      ) : (
                        rows.map((row) => (
                          <QuestionnaireInlineCard
                            key={row.id}
                            row={row}
                            name={questionnaireName(
                              row.forms_template_id,
                              formsTemplates,
                              i18n.language,
                              t('treatment_courses.q_unnamed'),
                            )}
                            startsAt={draft.startsAt}
                            language={i18n.language}
                            disabled={!editable}
                            onChange={(next) =>
                              setDraft({
                                ...draft,
                                automations: draft.automations.map((item) =>
                                  item.id === row.id ? next : item,
                                ),
                              })
                            }
                            onRemove={() =>
                              setDraft({
                                ...draft,
                                automations: draft.automations.filter((item) => item.id !== row.id),
                              })
                            }
                          />
                        ))
                      )}
                    </div>
                  );
                })}
                {templatesLoading ? <p className="hint">{t('app.loading')}</p> : null}
              </div>
            </section>
          </div>
        </div>
      ) : (
        <p className="hint">{t('treatment_courses.patient_pick_step')}</p>
      )}

      {selected && draft && editable && changeCount > 0 ? (
        <div
          className="savebar"
          role="region"
          aria-label={t('treatment_courses.patient_save_step')}
        >
          <div className="st">
            <span
              className="dot"
              aria-hidden
              style={{ background: changeCount > 0 ? 'var(--coral)' : 'var(--line-strong)' }}
            />
            <b>{t('treatment_courses.savebar_dirty', { count: changeCount })}</b>
            {timeChanged && draft.startsAt ? (
              <span className="num">
                ·{' '}
                {t('treatment_courses.savebar_new_time', {
                  when: formatCourseDateTime(draft.startsAt, i18n.language),
                })}
                {outsideDays != null && outsideDays > 0
                  ? ` (${t('treatment_courses.out_of_window').toLowerCase()})`
                  : ''}
              </span>
            ) : null}
          </div>
          <div className="r">
            <Button
              type="button"
              variant="tertiary"
              onClick={() => baseline && setDraft(baseline)}
              disabled={changeCount === 0}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => void persist(draft)}
              disabled={changeCount === 0 || saveStep.isPending}
            >
              <Check className="i sm" aria-hidden />
              {t('treatment_courses.patient_save_step')}
            </Button>
          </div>
        </div>
      ) : null}

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title={t('treatment_courses.add_step')}
      >
        <label className="block">
          <span className={FORM_LABEL_TYPE}>{t('treatment_courses.field_service')}</span>
          <Select
            className="mt-1"
            value={addServiceId}
            onChange={(event) => setAddServiceId(event.target.value)}
          >
            <option value="">{t('treatment_courses.service_placeholder')}</option>
            {(services ?? [])
              .filter((service) => !service.is_blocker)
              .map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))}
          </Select>
        </label>
        <div className="mt-s4 flex justify-end gap-s2">
          <Button type="button" variant="secondary" onClick={() => setAddOpen(false)}>
            {t('treatment_courses.patient_close')}
          </Button>
          <Button
            type="button"
            disabled={!addServiceId || addStep.isPending}
            onClick={async () => {
              try {
                const next = await addStep.mutateAsync({
                  courseId: course.id,
                  payload: { service_id: addServiceId },
                });
                const created = next.steps[next.steps.length - 1];
                if (created) setSelectedId(created.id);
                setAddOpen(false);
                setAddServiceId('');
                toast.success(t('treatment_courses.patient_toast_step_added'));
              } catch (err) {
                console.error(err);
                toast.error(t('treatment_courses.patient_toast_error'));
              }
            }}
          >
            {t('treatment_courses.add_step')}
          </Button>
        </div>
      </Modal>

      <ConfirmActionModal
        open={Boolean(cancelTarget)}
        title={t('treatment_courses.patient_cancel_step')}
        description={t('treatment_courses.patient_cancel_step_hint')}
        confirmLabel={t('treatment_courses.patient_cancel_step')}
        onClose={() => setCancelTarget(null)}
        onConfirm={async () => {
          if (!cancelTarget) return;
          try {
            await cancelStep.mutateAsync({ stepId: cancelTarget.id });
            toast.success(t('treatment_courses.patient_toast_step_cancelled'));
          } catch (err) {
            console.error(err);
            toast.error(t('treatment_courses.patient_toast_error'));
          } finally {
            setCancelTarget(null);
          }
        }}
      />

      <ConfirmActionModal
        open={Boolean(removeTarget)}
        title={t('treatment_courses.remove_step')}
        description={t('treatment_courses.patient_remove_step_hint')}
        confirmLabel={t('treatment_courses.remove_step')}
        onClose={() => setRemoveTarget(null)}
        onConfirm={async () => {
          if (!removeTarget) return;
          try {
            await removeStep.mutateAsync({ stepId: removeTarget.id });
            toast.success(t('treatment_courses.patient_toast_step_removed'));
          } catch (err) {
            console.error(err);
            toast.error(t('treatment_courses.patient_toast_error'));
          } finally {
            setRemoveTarget(null);
          }
        }}
      />
    </CourseTabPanel>
  );
}

function PatientQuestionnairePicker({
  open,
  phase,
  step,
  stepIndex,
  stepCount,
  templates,
  language,
  lockedIds,
  onClose,
  onConfirm,
}: {
  open: boolean;
  phase: QuestionnairePhase;
  step: BookedTreatmentCourseStep;
  stepIndex: number;
  stepCount: number;
  templates: FormsTemplateSummary[];
  language: string;
  lockedIds: readonly string[];
  onClose: () => void;
  onConfirm: (ids: string[]) => void;
}) {
  const { t } = useTranslation();
  const restricted = step.allowed_forms_template_ids.length > 0;
  let phaseLabel: string;
  switch (phase) {
    case 'after_appointment':
      phaseLabel = t('treatment_courses.q_after');
      break;
    case 'before_appointment':
      phaseLabel = t('treatment_courses.q_before');
      break;
    default: {
      const _exhaustive: never = phase;
      phaseLabel = _exhaustive;
    }
  }
  return (
    <QuestionnaireMultiPicker
      open={open}
      title={t('treatment_courses.add_questionnaire')}
      subtitle={t(
        restricted
          ? 'treatment_courses.picker_subtitle_step'
          : 'treatment_courses.picker_subtitle_step_free',
        { phase: phaseLabel, n: stepIndex, total: stepCount },
      )}
      items={toQuestionnairePickerItems(templates, language, t('treatment_courses.q_unnamed'))}
      lockedIds={lockedIds}
      showSearch={!restricted}
      note={
        restricted
          ? {
              title: t('treatment_courses.picker_lock_title'),
              body: t('treatment_courses.picker_lock_body'),
            }
          : null
      }
      onClose={onClose}
      onConfirm={onConfirm}
    />
  );
}

function QuestionnaireInlineCard({
  row,
  name,
  startsAt,
  language,
  disabled,
  onChange,
  onRemove,
}: {
  row: QuestionnaireAutomationDraft;
  name: string;
  startsAt: string | null;
  language: string;
  disabled: boolean;
  onChange: (next: QuestionnaireAutomationDraft) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const display = minutesToDisplay(row.offset_minutes);
  const sendDate = startsAt
    ? formatCourseDate(
        new Date(
          new Date(startsAt).getTime() +
            (row.phase === 'before_appointment' ? -1 : 1) * row.offset_minutes * 60_000,
        ).toISOString(),
        language,
      )
    : '';
  return (
    <div className="qcard">
      <div className="grow">
        <div className="t">{name}</div>
        <div className="m num">
          {sendDate
            ? t('treatment_courses.q_sends_on', { when: sendDate })
            : t('treatment_courses.q_unnamed')}
        </div>
      </div>
      <div className="ivl-ed num">
        <span>{t('treatment_courses.q_send')}</span>
        <input
          className={cn(FIELD_CONTROL, 'n')}
          value={display.value}
          disabled={disabled}
          aria-label={t('treatment_courses.field_interval')}
          onChange={(event) =>
            onChange({
              ...row,
              offset_minutes: displayToMinutes(Number(event.target.value) || 0, display.unit),
            })
          }
        />
        <Select
          value={display.unit}
          disabled={disabled}
          onChange={(event) =>
            onChange({
              ...row,
              offset_minutes: displayToMinutes(display.value, event.target.value as TimingUnit),
            })
          }
        >
          <option value="days">
            {display.value === 1
              ? t('treatment_courses.interval_unit_days_one')
              : t('treatment_courses.interval_unit_days_other')}
          </option>
          <option value="hours">{t('treatment_courses.interval_unit_hours', 'timer')}</option>
        </Select>
        <span>
          {row.phase === 'after_appointment'
            ? t('treatment_courses.q_after_appointment')
            : t('treatment_courses.q_before_appointment')}
        </span>
      </div>
      <Button type="button" variant="tertiary" size="sm" disabled={disabled} onClick={onRemove}>
        <X className="i sm" aria-hidden />
        {t('common.remove')}
      </Button>
    </div>
  );
}
